// Goods In › Sourcing (Gabe 2026-11-03). Stock bought through Flow's
// Sourcing module arrives from Alibaba suppliers with a 4-character PO ref on
// the courier label (e.g. KJDW). Three steps on one page:
//
//   1. Find   type or scan the ref → the PO's products, one row per variant.
//   2. Label  per variant: "Yes, barcoded" maps the supplier barcode to the
//             product (an importer sticker prints per unit); "No barcode"
//             prints our label (the variant SKU) per unit. Both default to
//             the ordered quantity. The BE creates the Portal product, the
//             supplier and the PO's manifest row.
//   3. Book   the BF scan sheet in sourcing mode (HOT, COLD, MEASURE), then
//             Complete sends the booked units to Flow as received.
//
// Plain form fields rather than HiddenScanInput, as on SKU Finder: the
// operator TYPES the ref or a quantity as often as they scan, and a hardware
// scanner fills the focused field like a keyboard anyway.
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Barcode,
    CheckCircle,
    ImageOff,
    Loader2,
    PackageCheck,
    Printer,
    RotateCcw,
    ScanBarcode,
    Search,
    Tag,
} from "lucide-react";

import { apiFetch } from "../api/apiFetch";
import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import { useAccess } from "../access/AccessContext";
import BfScanSheet from "../components/bf-scan/BfScanSheet";
import PrinterSelectModal from "../components/PrinterSelectModal";
import { detectPrintMode } from "../components/helpers/networkPrint";
import { playError, playSuccess } from "../components/helpers/audioPlayer";
import { printSourcingLabels, type SourcingImporter } from "../components/helpers/sourcingLabel";
import { sameWarehouse } from "../components/helpers/warehouseAlias";
import "../styles/scansheet.css";
import "../styles/sourcing.css";

export interface SourcingUnit {
    sku: string | null;
    lineId: number;
    variantId: number | null;
    lineStatus: string;
    title: string | null;
    variantLabel: string | null;
    supplier: string | null;
    qtyOrdered: number;
    imageUrl: string | null;
    prepared: { barcoded: boolean; supplierBarcode: string | null; labelsPrinted: number } | null;
    booked: number;
    overridden: number;
}

export interface SourcingPoView {
    po: { ref: string; status: string; supplierName: string | null };
    receipt: { status: string; completedAt: string | null; completedByName: string | null; flowError: string | null } | null;
    units: SourcingUnit[];
    offPo: { sku: string; units: number }[];
    importer: SourcingImporter;
}

type Stage = "find" | "po" | "book";
type PrintMode = "local" | "network" | null;
type Notice = { kind: "ok" | "error"; key: string; params?: Record<string, string | number> };

// The same rules as Flow's normaliseRef (and the BE's): upper-case, drop a
// leading "REF" or the courier label's "JLI TRADING LTD", spaces and dashes.
const REF_RE = /^[2346789ABCDEFGHJKMNPQRTUVWXYZ]{4}$/;
export const normaliseRef = (input: string): string | null => {
    const s = input.trim().toUpperCase()
        .replace(/^JLI\s*TRADING\s*(LTD|LIMITED)?[\s:#-]*/, "")
        .replace(/^REF[\s:#-]*/, "")
        .replace(/[\s-]/g, "");
    return REF_RE.test(s) ? s : null;
};

export const SOURCING_WAREHOUSE = "Pembroke";

const PO_REFUSALS: Record<string, string> = {
    po_not_found: "sourcing_po_not_found",
    po_cancelled: "sourcing_po_cancelled",
    po_not_placed: "sourcing_po_not_placed",
    invalid_ref: "sourcing_invalid_ref",
    sourcing_not_here: "sourcing_not_here",
};

const PREPARE_REFUSALS: Record<string, string> = {
    barcode_in_use: "sourcing_barcode_in_use",
    barcode_is_sku: "sourcing_barcode_is_sku",
    barcode_required: "sourcing_barcode_required",
    flow_unavailable: "sourcing_flow_unavailable",
};

const TEAM_LEADER_PLUS = ["Admin", "Manager", "Supervisor", "Team Leader"];

const readBody = async (res: Response): Promise<any> => {
    try { return await res.json(); } catch { return {}; }
};

const totalsOf = (units: SourcingUnit[]) => units.reduce(
    (acc, u) => ({ ordered: acc.ordered + u.qtyOrdered, booked: acc.booked + u.booked }),
    { ordered: 0, booked: 0 },
);

const Sourcing = () => {
    usePageTitle("sourcing");
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { authoritative, canWrite } = useAccess();

    const user = useMemo(() => {
        try { return JSON.parse(window.localStorage.getItem("user") ?? "null") ?? {}; } catch { return {}; }
    }, []);
    const warehouse: string = user.warehouse ?? "";
    // Sourcing stock is received at Pembroke Dock only (Gabe, 2026-11-04);
    // the server refuses everywhere else too.
    const sourcingHere = sameWarehouse(warehouse, SOURCING_WAREHOUSE);
    const canOverride = authoritative ? canWrite("goods_in.sourcing_override") : TEAM_LEADER_PLUS.includes(user.permissions);
    const canEditImporter = authoritative ? canWrite("admin.settings") : user.permissions === "Admin";

    const [stage, setStage] = useState<Stage>("find");
    const [refInput, setRefInput] = useState("");
    const [view, setView] = useState<SourcingPoView | null>(null);
    const [loading, setLoading] = useState(false);
    const [notice, setNotice] = useState<Notice | null>(null);
    const [selectedSku, setSelectedSku] = useState<string | null>(null);
    const [printMode, setPrintMode] = useState<PrintMode>(null);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);
    const refInputRef = useRef<HTMLInputElement | null>(null);
    const lastRefreshRef = useRef(0);

    useEffect(() => {
        let alive = true;
        detectPrintMode().then(mode => { if (alive) setPrintMode(mode); }).catch(() => {});
        return () => { alive = false; };
    }, []);

    const loadPo = useCallback(async (ref: string, { quiet = false } = {}): Promise<boolean> => {
        if (!quiet) setLoading(true);
        try {
            const res = await apiFetch(`/sourcing/po/${encodeURIComponent(ref)}`);
            const data = await readBody(res);
            if (!res.ok) {
                if (!quiet) {
                    setNotice({ kind: "error", key: PO_REFUSALS[data?.reason] ?? "sourcing_server_error", params: { ref } });
                    playError();
                }
                return false;
            }
            setView(data as SourcingPoView);
            return true;
        } catch {
            if (!quiet) {
                setNotice({ kind: "error", key: "sourcing_server_error" });
                playError();
            }
            return false;
        } finally {
            if (!quiet) setLoading(false);
        }
    }, []);

    const findPo = async (event: FormEvent) => {
        event.preventDefault();
        const ref = normaliseRef(refInput);
        if (!ref) {
            setNotice({ kind: "error", key: "sourcing_invalid_ref" });
            playError();
            return;
        }
        setNotice(null);
        if (await loadPo(ref)) {
            setRefInput("");
            setStage("po");
            playSuccess();
        }
    };

    const ref = view?.po.ref ?? null;
    const totals = useMemo(() => totalsOf(view?.units ?? []), [view]);
    const anyPrepared = (view?.units ?? []).some(u => u.prepared);
    const selected = view?.units.find(u => u.sku === selectedSku) ?? null;

    // Each unit confirmed on the sheet refreshes the progress, at most every 3 s.
    const refreshProgress = useCallback(() => {
        if (!ref) return;
        const now = Date.now();
        if (now - lastRefreshRef.current < 3000) return;
        lastRefreshRef.current = now;
        void loadPo(ref, { quiet: true });
    }, [loadPo, ref]);

    const completePo = async () => {
        if (!ref) return;
        if (!window.confirm(t("sourcing_complete_confirm", { ref, booked: totals.booked, ordered: totals.ordered }))) return;
        setLoading(true);
        try {
            const res = await apiFetch(`/sourcing/po/${encodeURIComponent(ref)}/complete`, { method: "POST" });
            const data = await readBody(res);
            if (!res.ok) {
                setNotice(data?.reason === "flow_write_failed"
                    ? { kind: "error", key: "sourcing_complete_failed", params: { message: String(data?.message ?? "") } }
                    : { kind: "error", key: data?.reason === "nothing_prepared" ? "sourcing_nothing_prepared" : "sourcing_server_error" });
                playError();
                return;
            }
            setNotice({ kind: "ok", key: "sourcing_completed", params: { ref, booked: Number(data?.totals?.received) || 0, ordered: Number(data?.totals?.ordered) || 0 } });
            playSuccess();
            await loadPo(ref, { quiet: true });
        } catch {
            setNotice({ kind: "error", key: "sourcing_server_error" });
            playError();
        } finally {
            setLoading(false);
        }
    };

    const leavePo = () => {
        setView(null);
        setSelectedSku(null);
        setNotice(null);
        setStage("find");
        setTimeout(() => refInputRef.current?.focus(), 0);
    };

    if (stage === "book" && ref && view) {
        return (
            <BfScanSheet
                location={{ barcode: "SOURCING", position: "", warehouse, bargainfox: true }}
                resumeFromCache={false}
                onLeave={() => { setStage("po"); void loadPo(ref, { quiet: true }); }}
                onBack={() => { setStage("po"); void loadPo(ref, { quiet: true }); }}
                sourcing={{
                    ref,
                    canOverride,
                    onBooked: refreshProgress,
                    banner: (
                        <div className="sourcing-banner">
                            <span className="sourcing-banner-ref">{t("sourcing_po_label", { ref })}</span>
                            <span>{t("sourcing_booked_of", totals)}</span>
                            <button type="button" className="btn btn-secondary dispatch-modal-btn" onClick={() => setStage("po")}>
                                {t("sourcing_change_po")}
                            </button>
                        </div>
                    ),
                }}
            />
        );
    }

    return (
        <div className="scan-panel sourcing-panel">
            <div className="page-title-bar">
                <h1 className="page-title">{t("sourcing")}</h1>
                <button
                    type="button"
                    className={`sourcing-printer-btn ${printMode ? "is-online" : ""}`}
                    onClick={() => setShowPrinterSelect(true)}
                    aria-label={t("sourcing_printer")}
                    title={printMode ? t("sourcing_printer") : t("sourcing_no_printer")}
                >
                    <Printer size={20} />
                </button>
                <button className="page-back-btn" onClick={() => (stage === "po" ? leavePo() : navigate("/goods-in"))} aria-label={t("back")}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            <div className="sourcing-body">
                {notice && (
                    <div className={`sourcing-notice sourcing-notice--${notice.kind}`} role={notice.kind === "error" ? "alert" : "status"}>
                        {t(notice.key, notice.params)}
                    </div>
                )}

                {stage === "find" && !sourcingHere && (
                    <div className="sourcing-notice sourcing-notice--error" role="status">
                        {t("sourcing_not_here")}
                    </div>
                )}

                {stage === "find" && sourcingHere && (
                    <form className="sourcing-card sourcing-find" onSubmit={findPo}>
                        <label className="sourcing-label" htmlFor="sourcing-ref">{t("sourcing_enter_po")}</label>
                        <input
                            id="sourcing-ref"
                            ref={refInputRef}
                            className="sourcing-input sourcing-ref-input"
                            value={refInput}
                            onChange={e => setRefInput(e.target.value)}
                            placeholder={t("sourcing_enter_po_placeholder")}
                            autoFocus
                            autoComplete="off"
                            autoCapitalize="characters"
                            spellCheck={false}
                            disabled={loading}
                        />
                        <span className="sourcing-hint">{t("sourcing_enter_po_hint")}</span>
                        <div className="sourcing-actions">
                            <button type="submit" className="btn btn-primary" disabled={loading || !refInput.trim()}>
                                {loading ? <Loader2 size={16} className="spin" /> : <Search size={16} />}
                                <span>{t("sourcing_find")}</span>
                            </button>
                        </div>
                    </form>
                )}

                {stage === "po" && view && (
                    <>
                        <div className="sourcing-card sourcing-po-head">
                            <div className="sourcing-po-ref">{t("sourcing_po_label", { ref: view.po.ref })}</div>
                            <div className="sourcing-po-meta">
                                <span>{t("sourcing_supplier")}: <strong>{view.po.supplierName ?? "—"}</strong></span>
                                <span className="sourcing-pill">{t(`sourcing_status_${view.po.status}`)}</span>
                            </div>
                            <div className="sourcing-po-meta">
                                <span>{t("sourcing_units_count", { count: view.units.length })}</span>
                                <span>{t("sourcing_booked_of", totals)}</span>
                            </div>
                            {view.receipt?.completedAt && (
                                <div className="sourcing-hint">
                                    {t("sourcing_received_by", {
                                        when: new Date(view.receipt.completedAt).toLocaleString(),
                                        name: view.receipt.completedByName ?? "",
                                    })}
                                </div>
                            )}
                            {!view.importer.name && !view.importer.address && (
                                <div className="sourcing-notice sourcing-notice--error">{t("sourcing_importer_missing")}</div>
                            )}
                            <div className="sourcing-actions">
                                <button
                                    type="button"
                                    className="btn btn-primary dispatch-modal-btn"
                                    disabled={loading || !anyPrepared}
                                    title={anyPrepared ? undefined : t("sourcing_book_in_hint")}
                                    onClick={() => setStage("book")}
                                >
                                    <ScanBarcode size={14} />
                                    <span>{t("sourcing_book_in")}</span>
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary dispatch-modal-btn"
                                    disabled={loading || !anyPrepared}
                                    onClick={completePo}
                                >
                                    <PackageCheck size={14} />
                                    <span>{t("sourcing_complete")}</span>
                                </button>
                                <button type="button" className="btn btn-ghost dispatch-modal-btn" onClick={leavePo}>
                                    {t("sourcing_change_po")}
                                </button>
                            </div>
                        </div>

                        <ul className="sourcing-units">
                            {view.units.map(unit => (
                                <li key={`${unit.lineId}-${unit.variantId ?? "line"}`}>
                                    <button
                                        type="button"
                                        className={`sourcing-unit ${unit.sku === selectedSku ? "is-selected" : ""}`}
                                        disabled={!unit.sku}
                                        onClick={() => setSelectedSku(unit.sku === selectedSku ? null : unit.sku)}
                                    >
                                        <UnitThumb url={unit.imageUrl} />
                                        <span className="sourcing-unit-text">
                                            <span className="sourcing-unit-title">{unit.title ?? unit.sku}</span>
                                            {unit.variantLabel && <span className="sourcing-unit-variant">{unit.variantLabel}</span>}
                                            <span className="sourcing-unit-sku">{unit.sku ?? t("sourcing_no_sku")}</span>
                                        </span>
                                        <span className="sourcing-unit-side">
                                            <span className={`sourcing-pill ${unit.prepared ? "sourcing-pill--done" : ""}`}>
                                                {unit.prepared
                                                    ? t(unit.prepared.barcoded ? "sourcing_barcode_mapped" : "sourcing_labelled")
                                                    : t("sourcing_to_label")}
                                            </span>
                                            <span className="sourcing-unit-count">
                                                {t("sourcing_booked_of", { booked: unit.booked, ordered: unit.qtyOrdered })}
                                            </span>
                                        </span>
                                    </button>
                                    {selected && unit.sku === selectedSku && ref && (
                                        <PreparePanel
                                            poRef={ref}
                                            unit={selected}
                                            importer={view.importer}
                                            printMode={printMode}
                                            onNeedPrinter={() => setShowPrinterSelect(true)}
                                            onNotice={setNotice}
                                            onDone={() => loadPo(ref, { quiet: true })}
                                        />
                                    )}
                                </li>
                            ))}
                        </ul>

                        {view.offPo.length > 0 && (
                            <div className="sourcing-card">
                                <div className="sourcing-label">{t("sourcing_off_po")}</div>
                                <div className="sourcing-hint">
                                    {t("sourcing_off_po_hint", { count: view.offPo.reduce((n, row) => n + row.units, 0) })}
                                </div>
                                <ul className="sourcing-off-po">
                                    {view.offPo.map(row => <li key={row.sku}>{row.sku} × {row.units}</li>)}
                                </ul>
                            </div>
                        )}

                        {canEditImporter && (
                            <ImporterEditor warehouse={warehouse} importer={view.importer} onSaved={() => loadPo(view.po.ref, { quiet: true })} />
                        )}
                    </>
                )}
            </div>

            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={Boolean(printMode)}
                    printMode={printMode}
                    onSelect={() => { setPrintMode("network"); setShowPrinterSelect(false); }}
                    onConnectLocal={() => { setPrintMode("local"); setShowPrinterSelect(false); }}
                    onDisconnect={() => { setPrintMode(null); setShowPrinterSelect(false); }}
                    onClose={() => setShowPrinterSelect(false)}
                />
            )}
        </div>
    );
};

const UnitThumb = ({ url }: { url: string | null }) => {
    const [failed, setFailed] = useState(false);
    if (!url || failed) return <span className="sourcing-thumb"><ImageOff size={22} /></span>;
    return <img className="sourcing-thumb" src={url} alt="" loading="lazy" onError={() => setFailed(true)} />;
};

interface PreparePanelProps {
    poRef: string;
    unit: SourcingUnit;
    importer: SourcingImporter;
    printMode: PrintMode;
    onNeedPrinter: () => void;
    onNotice: (notice: Notice | null) => void;
    onDone: () => Promise<unknown>;
}

// One variant: barcoded? → map + importer stickers, or our labels. Already
// prepared → reprint (or switch, e.g. the barcode turned out unreadable).
export const PreparePanel = ({ poRef, unit, importer, printMode, onNeedPrinter, onNotice, onDone }: PreparePanelProps) => {
    const { t } = useTranslation();
    const [choice, setChoice] = useState<"barcoded" | "label" | null>(
        unit.prepared ? (unit.prepared.barcoded ? "barcoded" : "label") : null,
    );
    const [barcode, setBarcode] = useState(unit.prepared?.supplierBarcode ?? "");
    const [qty, setQty] = useState(String(Math.max(1, unit.qtyOrdered)));
    const [busy, setBusy] = useState(false);
    const barcodeRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (choice === "barcoded" && !unit.prepared) barcodeRef.current?.focus();
    }, [choice, unit.prepared]);

    const sku = unit.sku as string;
    const copies = Math.max(0, Math.min(5000, Math.floor(Number(qty) || 0)));
    const labelText = { importedBy: t("sourcing_label_imported_by"), batch: t("sourcing_label_batch") };

    const print = async (kind: "label" | "sticker"): Promise<boolean> => {
        if (copies < 1) return true;
        if (!printMode) {
            onNotice({ kind: "error", key: "sourcing_no_printer" });
            onNeedPrinter();
            playError();
            return false;
        }
        const r = await printSourcingLabels(
            kind,
            { sku, title: unit.title, variantLabel: unit.variantLabel, batch: poRef },
            importer,
            labelText,
            copies,
            printMode,
        );
        if (!r.success) {
            onNotice({ kind: "error", key: "sourcing_print_failed", params: { error: r.error ?? "" } });
            playError();
            return false;
        }
        await apiFetch(`/sourcing/po/${encodeURIComponent(poRef)}/labels`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sku, qty: copies }),
        }).catch(() => {});
        onNotice({ kind: "ok", key: "sourcing_printed", params: { count: copies } });
        playSuccess();
        return true;
    };

    const prepareAndPrint = async (event?: FormEvent) => {
        event?.preventDefault();
        if (!choice) return;
        const scanned = barcode.trim();
        if (choice === "barcoded" && !scanned) {
            onNotice({ kind: "error", key: "sourcing_barcode_required" });
            playError();
            return;
        }
        setBusy(true);
        onNotice(null);
        try {
            const res = await apiFetch(`/sourcing/po/${encodeURIComponent(poRef)}/prepare`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(choice === "barcoded" ? { sku, barcoded: true, barcode: scanned } : { sku, barcoded: false }),
            });
            const data = await readBody(res);
            if (!res.ok) {
                onNotice({
                    kind: "error",
                    key: PREPARE_REFUSALS[data?.reason] ?? "sourcing_server_error",
                    params: { sku: String(data?.sku ?? "") },
                });
                playError();
                return;
            }
            await print(choice === "barcoded" ? "sticker" : "label");
            await onDone();
        } catch {
            onNotice({ kind: "error", key: "sourcing_server_error" });
            playError();
        } finally {
            setBusy(false);
        }
    };

    const reprint = async () => {
        setBusy(true);
        try {
            if (await print(unit.prepared?.barcoded ? "sticker" : "label")) await onDone();
        } finally {
            setBusy(false);
        }
    };

    const preparedSameWay = unit.prepared
        && (unit.prepared.barcoded ? choice === "barcoded" && barcode.trim() === (unit.prepared.supplierBarcode ?? "") : choice === "label");

    return (
        <form className="sourcing-prepare" onSubmit={prepareAndPrint}>
            <div className="sourcing-label">{t("sourcing_is_barcoded")}</div>
            <div className="sourcing-choice">
                <button
                    type="button"
                    className={`btn dispatch-modal-btn ${choice === "barcoded" ? "btn-primary" : "btn-secondary"}`}
                    onClick={() => setChoice("barcoded")}
                    disabled={busy}
                >
                    <Barcode size={14} />
                    <span>{t("sourcing_barcoded_yes")}</span>
                </button>
                <button
                    type="button"
                    className={`btn dispatch-modal-btn ${choice === "label" ? "btn-primary" : "btn-secondary"}`}
                    onClick={() => setChoice("label")}
                    disabled={busy}
                >
                    <Tag size={14} />
                    <span>{t("sourcing_barcoded_no")}</span>
                </button>
            </div>

            {choice === "barcoded" && (
                <>
                    <label className="sourcing-label" htmlFor={`sourcing-barcode-${sku}`}>{t("sourcing_scan_supplier_barcode")}</label>
                    <input
                        id={`sourcing-barcode-${sku}`}
                        ref={barcodeRef}
                        className="sourcing-input"
                        value={barcode}
                        onChange={e => setBarcode(e.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                        disabled={busy}
                    />
                </>
            )}

            {choice && (
                <>
                    <label className="sourcing-label" htmlFor={`sourcing-qty-${sku}`}>
                        {t(choice === "barcoded" ? "sourcing_stickers_to_print" : "sourcing_labels_to_print")}
                    </label>
                    <input
                        id={`sourcing-qty-${sku}`}
                        className="sourcing-input sourcing-qty-input"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={5000}
                        value={qty}
                        onChange={e => setQty(e.target.value)}
                        disabled={busy}
                    />
                    {unit.prepared && unit.prepared.labelsPrinted > 0 && (
                        <span className="sourcing-hint">{t("sourcing_labels_printed", { count: unit.prepared.labelsPrinted })}</span>
                    )}
                    <div className="sourcing-actions">
                        {preparedSameWay ? (
                            <button type="button" className="btn btn-primary dispatch-modal-btn" onClick={reprint} disabled={busy || copies < 1}>
                                {busy ? <Loader2 size={14} className="spin" /> : <RotateCcw size={14} />}
                                <span>{t("sourcing_reprint")}</span>
                            </button>
                        ) : (
                            <button type="submit" className="btn btn-primary dispatch-modal-btn" disabled={busy}>
                                {busy ? <Loader2 size={14} className="spin" /> : <CheckCircle size={14} />}
                                <span>{t(choice === "barcoded" ? "sourcing_print_stickers" : "sourcing_print_labels", { count: copies })}</span>
                            </button>
                        )}
                    </div>
                </>
            )}
        </form>
    );
};

interface ImporterEditorProps {
    warehouse: string;
    importer: SourcingImporter;
    onSaved: () => Promise<unknown>;
}

// Admin only: the importer block printed on this warehouse's labels.
const ImporterEditor = ({ warehouse, importer, onSaved }: ImporterEditorProps) => {
    const { t } = useTranslation();
    const [name, setName] = useState(importer.name ?? "");
    const [address, setAddress] = useState(importer.address ?? "");
    const [contact, setContact] = useState(importer.contact ?? "");
    const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");

    const save = async (event: FormEvent) => {
        event.preventDefault();
        setState("saving");
        try {
            const res = await apiFetch(`/sourcing/importer/${encodeURIComponent(warehouse)}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, address, contact }),
            });
            setState(res.ok ? "saved" : "error");
            if (res.ok) await onSaved();
        } catch {
            setState("error");
        }
    };

    return (
        <details className="sourcing-card sourcing-importer">
            <summary>{t("sourcing_importer")}</summary>
            <form onSubmit={save}>
                <label className="sourcing-label" htmlFor="sourcing-importer-name">{t("sourcing_importer_name")}</label>
                <input id="sourcing-importer-name" className="sourcing-input" value={name} onChange={e => setName(e.target.value)} />
                <label className="sourcing-label" htmlFor="sourcing-importer-address">{t("sourcing_importer_address")}</label>
                <input id="sourcing-importer-address" className="sourcing-input" value={address} onChange={e => setAddress(e.target.value)} />
                <label className="sourcing-label" htmlFor="sourcing-importer-contact">{t("sourcing_importer_contact")}</label>
                <input id="sourcing-importer-contact" className="sourcing-input" value={contact} onChange={e => setContact(e.target.value)} />
                <div className="sourcing-actions">
                    <button type="submit" className="btn btn-primary dispatch-modal-btn" disabled={state === "saving"}>
                        {t("sourcing_importer_save")}
                    </button>
                    {state === "saved" && <span className="sourcing-hint">{t("sourcing_importer_saved")}</span>}
                    {state === "error" && <span className="sourcing-hint sourcing-hint--error">{t("sourcing_server_error")}</span>}
                </div>
            </form>
        </details>
    );
};

export default Sourcing;
